// AI Agents — your autonomous finance team.
// Dashboard = the agents' results/output · Agent Library = the roster you manage · Skills = capabilities
import { ReactNode, useState } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { SectionHead, Search, Segmented, Tabs, Toggle, hoverLift } from "@/components/primitives";
import { LineChart, BarChart } from "@/components/charts";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { fmtK } from "@/utils/format";
import { DATA, Agent, agentById } from "@/data";

const HEALTH_BADGE: Record<string, ReactNode> = {
  healthy: <span className="badge b-pos"><span className="dot" style={{ background: "var(--pos)" }} />Healthy</span>,
  attention: <span className="badge b-warn"><span className="dot" style={{ background: "var(--warn)" }} />Needs attention</span>,
};

const MonChips = ({ agent }: { agent: Agent }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
    <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Monitoring</span>
    {agent.monitoring.map((m) => <span key={m} className="chip" style={{ height: 22 }}>{m}</span>)}
  </span>
);

type OpenFn = (id: string, tab: string) => void;

/* ═══ DASHBOARD — the agents' results ═══════════════════════════════ */
const Dashboard = ({ onOpen }: { onOpen: OpenFn }) => {
  const AGENTS = DATA.agents;
  const healthy = AGENTS.filter((a) => a.health === "healthy").length;
  const attention = AGENTS.length - healthy;
  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }} className="fade-up">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Your AI finance team</div>
          <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-.015em" }}>Your finance team has already done the work</div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 3, maxWidth: 640 }}>
            Five agents — Strategy, CFO, Controller, FP&A and Bookkeeper — monitor your books around the clock. Review only what matters.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <span className="badge b-pos" style={{ height: 28 }}><span className="dot" style={{ background: "var(--pos)" }} />{healthy} healthy</span>
          {attention > 0 && <span className="badge b-warn" style={{ height: 28 }}><span className="dot" style={{ background: "var(--warn)" }} />{attention} needs attention</span>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {AGENTS.map((a) => (
          <div key={a.id} className="card" style={{ padding: 20, cursor: "pointer", transition: "box-shadow .15s,border-color .15s" }}
            onClick={() => { posthog.capture("agent_detail_opened", { agent: a.id, via: "dashboard_card" }); onOpen(a.id, "overview"); }} {...hoverLift}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ width: 40, height: 40, borderRadius: 11, background: a.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={a.icon} size={20} stroke={a.color} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 50, background: a.health === "healthy" ? "var(--pos)" : "var(--warn)", flexShrink: 0 }} />
                  <span style={{ fontSize: 15.5, fontWeight: 600 }}>{a.name}</span>
                  <span style={{ marginLeft: "auto" }}>{HEALTH_BADGE[a.health]}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 1 }}>{a.role}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.5, margin: "12px 0 14px" }}>{a.desc}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span className="num" style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-.02em", color: (a.headline as { tone?: string }).tone === "warn" ? "var(--warn)" : "var(--ink)" }}>{a.headline.value}</span>
              <span style={{ fontSize: 12, color: "var(--ink-4)" }}>{a.headline.label}</span>
            </div>
            <div style={{ background: "var(--surface-2)", border: "1px solid var(--line-2)", borderRadius: 10, padding: "11px 13px", margin: "14px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span className="eyebrow" style={{ fontSize: 10 }}>Last activity</span>
                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{a.last.time}</span>
              </div>
              <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.45 }}>{a.last.text}</div>
            </div>
            <div style={{ marginBottom: 14 }}><MonChips agent={a} /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 13, borderTop: "1px solid var(--line-2)" }}>
              <span style={{ fontSize: 12, color: "var(--pos)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span className="dot live-dot" style={{ width: 7, height: 7, borderRadius: 50, background: "var(--pos)" }} />Active now
              </span>
              <span className="link" style={{ fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}
                onClick={(e) => { e.stopPropagation(); posthog.capture("agent_detail_opened", { agent: a.id, via: "details_link" }); onOpen(a.id, "overview"); }}>
                View details <Icon name="chevright" size={14} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ AGENT DETAIL — Overview / Activity / Settings ═════════════════ */
const MiniStat = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) => (
  <div className="card" style={{ padding: "16px 18px" }}>
    <div className="eyebrow" style={{ fontSize: 10.5, marginBottom: 8 }}>{label}</div>
    <div className="num" style={{ fontSize: 23, fontWeight: 700, letterSpacing: "-.02em", color: tone === "pos" ? "var(--pos)" : tone === "warn" ? "var(--warn)" : "var(--ink)" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 5 }}>{sub}</div>}
  </div>
);

const StatusBanner = ({ tone, text, action }: { tone: "pos" | "warn"; text: string; action?: string }) => {
  const notify = useNotImplemented();
  return (
    <div className="card" style={{
      background: tone === "pos" ? "var(--pos-soft)" : "var(--warn-soft)",
      border: tone === "pos" ? "1px solid #bfe6d3" : "1px solid #f0d9a8",
      padding: "14px 18px", display: "flex", alignItems: "center", gap: 11,
    }}>
      <Icon name={tone === "pos" ? "check-circle" : "alert"} size={18} stroke={tone === "pos" ? "var(--pos)" : "var(--warn)"} />
      <span style={{ fontSize: 13, color: tone === "pos" ? "#0a6b47" : "#8a5a08", fontWeight: 500, flex: 1 }}>{text}</span>
      {action && <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("agent_banner_action_clicked", { action }); notify(action); }}>{action}</button>}
    </div>
  );
};

const ListRows = ({ rows, iconTile }: { rows: [string, string, string, string?][]; iconTile?: boolean }) => (
  <div>
    {rows.map((r, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: i < rows.length - 1 ? "1px solid var(--line-2)" : "none" }}>
        {iconTile && (
          <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={r[0]} size={16} stroke="var(--ink-3)" />
          </span>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: iconTile ? 500 : 600 }}>{r[iconTile ? 1 : 0]}</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{r[iconTile ? 2 : 1]}</div>
        </div>
        <span className="num" style={{ fontSize: 14, fontWeight: 600 }}>{r[iconTile ? 3 : 2]}</span>
      </div>
    ))}
  </div>
);

const AgentOverview = ({ agent }: { agent: Agent }) => {
  const notify = useNotImplemented();
  if (agent.id === "cashflow") return <>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 16 }}>
      <MiniStat label="Current cash balance" value="$184,000" sub="Across 3 accounts" />
      <MiniStat label="Forecast" value="Positive" sub="for the next 90 days" tone="pos" />
      <MiniStat label="Risk level" value="Low" sub="buffer stays intact" tone="pos" />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, marginBottom: 16 }}>
      <div className="card">
        <div className="card-h">
          <div className="card-t">90-day cash forecast</div>
          <span style={{ fontSize: 12, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 16, height: 2, background: agent.color }} />Projected balance
          </span>
        </div>
        <div className="card-b">
          <div style={{ position: "relative" }}>
            <LineChart height={230} yFmt={(v) => fmtK(v)} labels={["Now", "", "", "+30d", "", "", "+60d", "", "", "+90d"]}
              series={[{ data: [184, 178, 190, 150, 168, 142, 158, 138, 150, 124].map((v) => v * 1000), color: agent.color }]} />
            <div style={{ position: "absolute", left: 48, right: 14, bottom: 56, borderTop: "1.5px dashed var(--warn)", pointerEvents: "none" }}>
              <span style={{ position: "absolute", right: 0, top: -16, fontSize: 11, color: "var(--warn)", fontWeight: 600 }}>Minimum buffer · $100k</span>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-h"><div className="card-t">Upcoming events</div></div>
        <ListRows iconTile rows={[["users", "Payroll", "due in 12 days", "$48,000"], ["receipt", "Vendor payments", "due in 19 days", "$31,000"], ["wallet", "Outstanding invoices", "3 collecting", "$73,000"]]} />
      </div>
    </div>
    <StatusBanner tone="pos" text="All protected obligations are funded across the horizon. No action needed right now — the agent is watching for changes." />
  </>;

  if (agent.id === "budget") return <>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 16 }}>
      <MiniStat label="Current month" value="June 2026" sub="20 days elapsed" />
      <MiniStat label="Total budget" value="$120,000" sub="planned this month" />
      <MiniStat label="Actual spend" value="$137,000" sub="vs $120,000 planned" />
      <MiniStat label="Variance" value="+14.2%" sub="$17,000 over" tone="warn" />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, marginBottom: 16 }}>
      <div className="card">
        <div className="card-h">
          <div className="card-t">Budget vs actual by category</div>
          <span style={{ display: "flex", gap: 14, fontSize: 12 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--ink-5)" }} />Budget</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--brand)" }} />Actual</span>
          </span>
        </div>
        <div className="card-b">
          <BarChart height={230} keys={["budget", "actual"]} colors={["var(--ink-5)", "var(--brand)"]} yFmt={(v) => fmtK(v * 1000)}
            data={[
              { label: "Payroll", budget: 60, actual: 60 }, { label: "Software", budget: 14, actual: 16 },
              { label: "Marketing", budget: 22, actual: 38 }, { label: "Contractors", budget: 12, actual: 14 },
              { label: "Operations", budget: 12, actual: 13 },
            ]} />
        </div>
      </div>
      <div className="card">
        <div className="card-h"><div className="card-t">Biggest drivers</div></div>
        <div>
          {[["LinkedIn Ads", "New Q3 campaign launched mid-month", "+$5,400"], ["HubSpot Renewal", "Annual plan tier upgrade", "+$2,000"], ["Agency Spend", "Extra creative sprint", "+$3,800"]].map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "15px 18px", borderBottom: i < 2 ? "1px solid var(--line-2)" : "none" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r[0]}</div>
                <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{r[1]}</div>
              </div>
              <span className="num" style={{ fontSize: 14, fontWeight: 700, color: "var(--warn)" }}>{r[2]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <StatusBanner tone="warn" action="Review"
      text="Marketing is 18% over budget, driven by new ad campaigns. Review LinkedIn Ads spend or raise the marketing budget for Q3." />
  </>;

  if (agent.id === "simulator") return <>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 16 }}>
      <MiniStat label="Base-case runway" value="18 mo" sub="above your 18-month target" tone="pos" />
      <MiniStat label="Scenarios saved" value="3" sub="Base · +3 hires · Downside" />
      <MiniStat label="Break-even" value="Q3 2027" sub="on the base case" />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, marginBottom: 16 }}>
      <div className="card">
        <div className="card-h"><div className="card-t">Runway under each scenario</div></div>
        <div className="card-b">
          <LineChart height={230} yFmt={(v) => fmtK(v)} labels={["Now", "", "", "+6mo", "", "", "+12mo", "", "", "+18mo"]}
            series={[{ data: [184, 176, 170, 160, 152, 144, 138, 130, 122, 116].map((v) => v * 1000), color: "#05686F" }]} />
        </div>
      </div>
      <div className="card">
        <div className="card-h"><div className="card-t">Saved scenarios</div></div>
        <div>
          {([["Base plan", "No changes to hiring", "18 mo", "pos"], ["+3 engineers Q3", "Adds $2.3k/mo burn", "14 mo", "warn"], ["Downside −20% rev", "Revenue softens", "11 mo", "neg"]] as const).map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: i < 2 ? "1px solid var(--line-2)" : "none" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r[0]}</div>
                <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{r[1]}</div>
              </div>
              <span className="num" style={{ fontSize: 14, fontWeight: 600, color: r[3] === "pos" ? "var(--pos)" : r[3] === "warn" ? "var(--warn)" : "var(--neg)" }}>{r[2]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <StatusBanner tone="pos" text="Every saved scenario keeps runway above your 12-month floor. The base plan holds at 18 months." />
  </>;

  // recon
  return <>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 16 }}>
      <MiniStat label="Matched today" value="37" sub="of 39 new transactions" />
      <MiniStat label="Auto-categorized" value="99.2%" sub="above 95% confidence" tone="pos" />
      <MiniStat label="Needs review" value="2" sub="category fixes drafted" tone="warn" />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, marginBottom: 16 }}>
      <div className="card">
        <div className="card-h"><div className="card-t">Recent matches</div><span className="badge b-pos">99.2% confidence</span></div>
        <div>
          {[["Stripe payout", "Revenue · Deposits", "+$22,500", "Cleared"], ["AWS", "Infrastructure", "−$12,340", "Matched"], ["Delta Airlines", "Travel", "−$1,240", "Matched"], ["Notion Labs", "Software", "−$480", "Matched"]].map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: i < 3 ? "1px solid var(--line-2)" : "none" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r[0]}</div>
                <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{r[1]}</div>
              </div>
              <span className="num" style={{ fontSize: 13, fontWeight: 600, color: r[2][0] === "+" ? "var(--pos)" : "var(--ink-2)" }}>{r[2]}</span>
              <span className="badge b-mute">{r[3]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="card-h"><div className="card-t">Drafted for review</div><span className="badge b-warn">2</span></div>
        <div>
          {[["Recategorize Figma", "Software → Design tools", "$144"], ["Split Uber charge", "Travel + Meals", "$86"]].map((r, i) => (
            <div key={i} style={{ padding: "14px 18px", borderBottom: i < 1 ? "1px solid var(--line-2)" : "none" }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r[0]}</div>
              <div style={{ fontSize: 11.5, color: "var(--ink-4)", margin: "2px 0 9px" }}>{r[1]} · {r[2]}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-pri btn-sm" onClick={() => { posthog.capture("agent_draft_action", { action: "approve", item: r[0] }); notify("Approve"); }}>Approve</button>
                <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("agent_draft_action", { action: "edit", item: r[0] }); notify("Edit draft"); }}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <StatusBanner tone="pos" text="Your books are 99.2% reconciled. Only 2 items need a quick look — everything else matched automatically." />
  </>;
};

const ACT_ICON: Record<string, string> = { alert: "var(--warn)", check: "var(--pos)", log: "var(--info)" };
const ACT_BADGE: Record<string, string> = { Pending: "b-warn", Accepted: "b-pos", Logged: "b-info" };

const AgentActivity = ({ agent }: { agent: Agent }) => {
  const notify = useNotImplemented();
  const groups = DATA.agentActivity[agent.id] || [];
  return (
    <div>
      {groups.map((g, gi) => (
        <div key={gi} style={{ marginBottom: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>{g.group}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {g.items.map((it, i) => (
              <div key={i} style={{ display: "flex", gap: 13 }}>
                <span style={{ width: 14, height: 14, borderRadius: 50, border: "2.5px solid " + ACT_ICON[it.kind], flexShrink: 0, marginTop: 4, background: "#fff" }} />
                <div className="card" style={{ flex: 1, padding: "15px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{it.title}</span>
                    <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{it.time}</span>
                    <span className={"badge " + ACT_BADGE[it.status]}>{it.status}</span>
                  </div>
                  {it.impact ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 12 }}>
                      <div>
                        <div className="eyebrow" style={{ fontSize: 10, marginBottom: 5 }}>Impact</div>
                        <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>{it.impact}</div>
                      </div>
                      <div>
                        <div className="eyebrow" style={{ fontSize: 10, marginBottom: 5 }}>Reasoning</div>
                        <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>{it.reasoning}</div>
                      </div>
                    </div>
                  ) : it.rec ? (
                    <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.5 }}>
                      <b style={{ color: "var(--ink-2)" }}>Recommended</b> · {it.rec}
                    </div>
                  ) : null}
                  {it.actions && (
                    <div style={{ marginTop: 12 }}>
                      <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Recommended actions</div>
                      <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 4 }}>
                        {it.actions.map((a, j) => <li key={j} style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>{a}</li>)}
                      </ul>
                    </div>
                  )}
                  {it.plan && (
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button className="btn btn-pri btn-sm" onClick={() => { posthog.capture("agent_plan_action", { agent: agent.id, action: "accept", item: it.title }); notify("Accept plan"); }}>Accept plan</button>
                      <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("agent_plan_action", { agent: agent.id, action: "dismiss", item: it.title }); notify("Dismiss"); }}>Dismiss</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { posthog.capture("agent_plan_action", { agent: agent.id, action: "snooze", item: it.title }); notify("Snooze"); }}>Snooze</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AgentSettings = ({ agent }: { agent: Agent }) => {
  const notify = useNotImplemented();
  return (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, maxWidth: 940 }}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>What should I watch?</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginBottom: 12 }}>The agent monitors and protects these first.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
        {(agent.id === "cashflow" ? ["Payroll", "Vendor payments", "Taxes", "Debt obligations"] : agent.monitoring).map((m) => (
          <label key={m} style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--brand-tint)", border: "1px solid var(--brand-line)", borderRadius: 9, padding: "11px 13px", cursor: "pointer" }}>
            <span style={{ width: 18, height: 18, borderRadius: 5, background: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="check" size={13} stroke="#fff" sw={2.5} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{m}</span>
          </label>
        ))}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{agent.id === "budget" ? "Budget alert threshold" : "Minimum cash buffer"}</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginBottom: 10 }}>The agent alerts you when this line is crossed.</div>
      <input defaultValue={agent.id === "budget" ? "10% over plan" : "$100,000"} className="focus-ring" style={{
        width: "100%", height: 42, padding: "0 14px", borderRadius: 9, border: "1px solid var(--line)",
        font: "inherit", fontSize: 14, fontWeight: 600, outline: "none", marginBottom: 24,
      }} />
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{agent.id === "budget" ? "Reporting period" : "Forecast horizon"}</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginBottom: 10 }}>
        {agent.id === "budget" ? "How the agent groups the numbers." : "How far ahead the agent projects."}
      </div>
      <div style={{ position: "relative" }}>
        <select defaultValue={agent.id === "budget" ? "Monthly" : "90 Days"} className="focus-ring" style={{
          width: "100%", height: 42, padding: "0 14px", borderRadius: 9, border: "1px solid var(--line)",
          font: "inherit", fontSize: 14, outline: "none", appearance: "none", background: "var(--surface)",
        }}>
          {(agent.id === "budget" ? ["Monthly", "Quarterly", "Annual"] : ["30 Days", "60 Days", "90 Days", "180 Days"]).map((o) => <option key={o}>{o}</option>)}
        </select>
        <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon name="chevdown" size={16} stroke="var(--ink-4)" />
        </span>
      </div>
    </div>
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>Notification preferences</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginBottom: 12 }}>Where alerts are delivered.</div>
      <div className="card" style={{ marginBottom: 24 }}>
        {([["In app", true], ["Email", true], ["Slack", true], ["WhatsApp", false]] as const).map((r, i) => (
          <div key={r[0]} style={{ display: "flex", alignItems: "center", padding: "13px 16px", borderBottom: i < 3 ? "1px solid var(--line-2)" : "none" }}>
            <span style={{ flex: 1, fontSize: 13.5 }}>{r[0]}</span>
            <Toggle on={r[1]} onClick={() => { posthog.capture("agent_notification_channel_toggled", { agent: agent.id, channel: r[0], to: !r[1] }); notify(`${r[0]} notifications`); }} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>Alert frequency</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginBottom: 12 }}>How often the agent reaches out.</div>
      <div style={{ marginBottom: 20 }}>
        <Segmented value="immediate" onChange={(v) => { posthog.capture("agent_alert_frequency_changed", { agent: agent.id, frequency: v }); notify("Alert frequency"); }} options={[{ value: "immediate", label: "Immediate" }, { value: "daily", label: "Daily digest" }, { value: "weekly", label: "Weekly summary" }]} />
      </div>
      <div className="card" style={{ background: "var(--surface-2)", padding: "13px 15px", display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Icon name="shield" size={16} stroke="var(--ink-4)" style={{ marginTop: 1, flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
          This agent never moves money or edits records on its own. It only recommends — you approve every action.
        </span>
      </div>
    </div>
    <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
      <button className="btn btn-sec" onClick={() => { posthog.capture("agent_settings_cancelled", { agent: agent.id }); notify("Cancel"); }}>Cancel</button>
      <button className="btn btn-pri" onClick={() => { posthog.capture("agent_settings_saved", { agent: agent.id }); notify("Save changes"); }}>Save changes</button>
    </div>
  </div>
  );
};

export const AgentDetail = ({ agentId, initTab, onBack }: { agentId: string; initTab?: string; onBack: () => void }) => {
  const notify = useNotImplemented();
  const agent = agentById(agentId);
  const [tab, setTab] = useState(initTab || "overview");
  const [paused, setPaused] = useState(false);
  if (!agent) return null;
  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }} className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink-4)", marginBottom: 14, cursor: "pointer" }} onClick={onBack}>
        <Icon name="chevright" size={13} stroke="var(--ink-4)" style={{ transform: "rotate(180deg)" }} />
        <span className="link" style={{ fontSize: 12.5 }}>Agents</span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18 }}>
        <span style={{ width: 48, height: 48, borderRadius: 12, background: agent.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={agent.icon} size={24} stroke={agent.color} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ width: 9, height: 9, borderRadius: 50, background: agent.health === "healthy" ? "var(--pos)" : "var(--warn)" }} />
            <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.015em" }}>{agent.name}</span>
            {HEALTH_BADGE[agent.health]}
            <span style={{ fontSize: 13, color: "var(--ink-4)" }}>· {agent.role}</span>
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", margin: "5px 0 8px" }}>{agent.desc}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MonChips agent={agent} />
            <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>· checked {agent.checked}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button className="btn btn-sec" onClick={() => { posthog.capture("agent_pause_toggled", { agent: agent.id, to: paused ? "resumed" : "paused" }); setPaused((p) => !p); }}>
            <Icon name={paused ? "play" : "pause"} size={15} stroke="var(--ink-3)" /> {paused ? "Resume" : "Pause"}
          </button>
          <button className="btn btn-sec btn-icon" onClick={() => { posthog.capture("agent_config_icon_clicked", { agent: agent.id }); notify("Agent configuration"); }}><Icon name="sliders" size={16} stroke="var(--ink-3)" /></button>
        </div>
      </div>
      <div style={{ marginBottom: 22 }}>
        <Tabs value={tab} onChange={(v) => { posthog.capture("agent_detail_tab_changed", { agent: agent.id, tab: v }); setTab(v); }} tabs={[{ value: "overview", label: "Overview" }, { value: "activity", label: "Activity" }, { value: "settings", label: "Settings" }]} />
      </div>
      {tab === "overview" && <AgentOverview agent={agent} />}
      {tab === "activity" && <AgentActivity agent={agent} />}
      {tab === "settings" && <AgentSettings agent={agent} />}
    </div>
  );
};

/* ═══ SKILLS ════════════════════════════════════════════════════════ */
const AUTON_STYLE: Record<string, string> = {
  "Fully autonomous": "b-pos", "Auto with approval": "b-info", "Approval required": "b-warn", "Draft only": "b-violet", "On request": "b-mute",
};

export const SkillsScreen = () => {
  const notify = useNotImplemented();
  const [cat, setCat] = useState("All");
  const cats = ["All", ...Array.from(new Set(DATA.skills.map((s) => s.cat)))];
  const rows = DATA.skills.filter((s) => cat === "All" || s.cat === cat);
  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Skills" size="lg"
        sub="The capabilities your agents can use. Turn skills on or off and set how much autonomy each one has."
        actions={<button className="btn btn-pri" onClick={() => { posthog.capture("skill_create_clicked"); notify("New skill"); }}><Icon name="plus" size={15} stroke="#fff" /> New skill</button>} />
      <div style={{ display: "flex", gap: 7, marginBottom: 16, flexWrap: "wrap" }}>
        {cats.map((c) => (
          <button key={c} onClick={() => { posthog.capture("skills_category_filtered", { category: c }); setCat(c); }} className={"chip" + (cat === c ? "" : " chip-out")}
            style={cat === c ? { background: "var(--brand)", color: "#fff" } : {}}>{c}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {rows.map((s) => (
          <div key={s.id} className="card" style={{ padding: 17 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: s.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={s.icon} size={19} stroke={s.color} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</span>
                  <span style={{ marginLeft: "auto" }}><Toggle on={s.on} onClick={() => { posthog.capture("skill_toggled", { skill: s.name, to: !s.on }); notify(`${s.name} skill`); }} /></span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 3, lineHeight: 1.5 }}>{s.desc}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 11, flexWrap: "wrap" }}>
                  <span className={"badge " + AUTON_STYLE[s.autonomy]}>{s.autonomy}</span>
                  <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>·</span>
                  <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{s.agents.join(", ")}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink-4)" }} className="num">{s.runs} runs</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ AGENT LIBRARY — the roster you manage ═════════════════════════ */
const Library = ({ onOpen }: { onOpen: OpenFn }) => {
  const notify = useNotImplemented();
  return (
  <div style={{ maxWidth: 1180, margin: "0 auto" }} className="fade-up">
    <SectionHead title="Agent Library" size="lg"
      sub="Your finance team and the agents you can add. Configure any agent, pause it, or hire a new one."
      actions={<><Search placeholder="Search agents" w={200} /><button className="btn btn-pri" onClick={() => { posthog.capture("agent_add_clicked"); notify("Add agent"); }}><Icon name="plus" size={15} stroke="#fff" /> Add agent</button></>} />
    <div className="eyebrow" style={{ marginBottom: 10 }}>On your team · {DATA.agents.length}</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14, marginBottom: 28 }}>
      {DATA.agents.map((a) => (
        <div key={a.id} className="card" style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
            <span style={{ width: 40, height: 40, borderRadius: 11, background: a.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={a.icon} size={20} stroke={a.color} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14.5, fontWeight: 600 }}>{a.name}</span>
                <span style={{ marginLeft: "auto" }}>{HEALTH_BADGE[a.health]}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 1 }}>{a.role}</div>
              <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.5 }}>{a.desc}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, paddingTop: 12, borderTop: "1px solid var(--line-2)" }}>
            <button className="btn btn-sec btn-sm" style={{ flex: 1 }} onClick={() => { posthog.capture("agent_detail_opened", { agent: a.id, via: "library_configure" }); onOpen(a.id, "settings"); }}>
              <Icon name="gear" size={14} stroke="var(--ink-3)" /> Configure
            </button>
            <button className="btn btn-sec btn-sm" style={{ flex: 1 }} onClick={() => { posthog.capture("agent_detail_opened", { agent: a.id, via: "library_details" }); onOpen(a.id, "overview"); }}>View details</button>
          </div>
        </div>
      ))}
    </div>
    <div className="eyebrow" style={{ marginBottom: 10 }}>Available to hire</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
      {DATA.libraryAvailable.map((a) => (
        <div key={a.id} className="card" style={{ padding: 18, textAlign: "center" }}>
          <span style={{ width: 46, height: 46, borderRadius: 12, background: a.color + "18", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 11 }}>
            <Icon name={a.icon} size={23} stroke={a.color} />
          </span>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{a.name}</div>
          <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5, margin: "5px 0 12px", minHeight: 52 }}>{a.desc}</div>
          <button className="btn btn-sec btn-sm" style={{ width: "100%" }} onClick={() => { posthog.capture("agent_hire_clicked", { agent: a.name }); notify(`Add ${a.name} to team`); }}><Icon name="plus" size={14} stroke="var(--ink-3)" /> Add to team</button>
        </div>
      ))}
    </div>
  </div>
  );
};

/* ═══ WRAPPERS — Dashboard & Library share the detail overlay ═══════ */
export const DashboardScreen = () => {
  const [detail, setDetail] = useState<{ id: string; tab: string } | null>(null);
  if (detail) return <AgentDetail agentId={detail.id} initTab={detail.tab} onBack={() => setDetail(null)} />;
  return <Dashboard onOpen={(id, tab) => setDetail({ id, tab })} />;
};

export const LibraryScreen = () => {
  const [detail, setDetail] = useState<{ id: string; tab: string } | null>(null);
  if (detail) return <AgentDetail agentId={detail.id} initTab={detail.tab} onBack={() => setDetail(null)} />;
  return <Library onOpen={(id, tab) => setDetail({ id, tab })} />;
};
